'use client';

/**
 * Intent Mode Widget — canvas variant switch for intent mode.
 *
 * @component_role Dispatches between the two intent-mode surfaces based
 *   on which response field is populated on `intent-store`:
 *
 *   - `gridResponse` set → IntentGridWidget (schedule-driven N-line)
 *   - otherwise          → IntentWidget (single-query text/image)
 *
 *   Also hosts the PDF drop target for the whole intent viewport — a
 *   dropped PDF stages onto `intent-store.pendingScheduleFile` and opens
 *   the Add Schedule tab (same form the sidebar button opens). Drop is
 *   active anywhere inside the widget, not just the empty state — a
 *   grid already loaded doesn't block bringing in a new schedule.
 *
 * @business_rule Both surfaces live under one mode; the switch is data-
 *   driven. A grid response doesn't clear the single-query response and
 *   vice versa — history holds both kinds. Whichever surface was last
 *   touched is what the buyer sees.
 *
 * @layer presentation
 * @arch_component_type widget
 */

import { useCallback, useState } from 'react';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useIntentStore } from '@/lib/stores/intent-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { cn } from '@/lib/utils/cn';
import { IntentGridWidget } from './intent-grid-widget';
import { IntentWidget } from './intent-widget';
import type { CanvasWidgetProps } from '@/lib/registry/types';

const MAX_PDF_BYTES = 10 * 1024 * 1024;

export function IntentModeWidget(props: CanvasWidgetProps) {
  const gridResponse = useIntentStore((s) => s.gridResponse);
  const stageScheduleFile = useIntentStore((s) => s.stageScheduleFile);
  const openTab = useTabStore((s) => s.openTab);

  const [dragActive, setDragActive] = useState(false);

  // PDF drop handoff to the Add Schedule form. Drop stages the file
  // (as File + base64) onto the intent store and opens the tab; the
  // form widget consumes on mount. Project, brief and room label are
  // still captured in the form — drop is a shortcut, not a bypass.
  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);
      const f = e.dataTransfer.files?.[0];
      if (!f) return;
      if (f.size > MAX_PDF_BYTES) {
        toast.error(`File too large (${(f.size / 1024 / 1024).toFixed(1)} MB > 10 MB)`);
        return;
      }
      const isPdf = /pdf/i.test(f.type) || /\.pdf$/i.test(f.name);
      if (!isPdf) {
        toast.error('Only PDF schedules are supported.');
        return;
      }
      const buf = await f.arrayBuffer();
      const b64 = btoa(
        new Uint8Array(buf).reduce((s, b) => s + String.fromCharCode(b), ''),
      );
      stageScheduleFile(f, b64);
      openTab({
        id: 'add-schedule-form',
        type: 'add_schedule',
        title: 'Add Schedule',
        entityId: 'add-schedule',
      });
    },
    [openTab, stageScheduleFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types?.includes('Files')) {
      e.preventDefault();
      e.stopPropagation();
      if (!dragActive) setDragActive(true);
    }
  }, [dragActive]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    // Only clear when leaving the widget itself, not when moving onto
    // a child. `relatedTarget` is the element the pointer is entering.
    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
    setDragActive(false);
  }, []);

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={cn(
        'h-full w-full relative',
        dragActive && 'ring-2 ring-primary ring-offset-2 ring-offset-background',
      )}
    >
      {gridResponse ? <IntentGridWidget {...props} /> : <IntentWidget {...props} />}
      {dragActive && (
        <div
          className={cn(
            'pointer-events-none absolute inset-0 z-50',
            'flex items-center justify-center',
            'bg-background/80 backdrop-blur-sm',
          )}
        >
          <div className="flex flex-col items-center gap-2 text-primary">
            <FileText className="w-10 h-10" />
            <p className="text-sm font-medium">Drop PDF to add a schedule</p>
          </div>
        </div>
      )}
    </div>
  );
}
